import { useState } from 'react'
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Switch,
  Radio,
  message,
  Popconfirm,
  Card,
  Empty,
} from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { userFeaturesApi } from '@/api/user-features'
import type { FeatureConfig } from '@/types/user'
import { getApiErrorMessage } from '@/utils/error'

interface UserFeaturesProps {
  userId: string
}

type FeatureType = 'boolean' | 'complex'

interface FeatureFormValues {
  featureKey: string
  featureType: FeatureType
  booleanValue: boolean
  enabled: boolean
  settings?: string
  metadata?: string
}

export const UserFeatures = ({ userId }: UserFeaturesProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingFeature, setEditingFeature] = useState<string | null>(null)
  const [form] = Form.useForm<FeatureFormValues>()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['user-features', userId],
    queryFn: async () => {
      const response = await userFeaturesApi.getFeatures(userId)
      return response.data.features
    },
  })

  const setFeatureMutation = useMutation({
    mutationFn: async ({
      featureKey,
      value,
    }: {
      featureKey: string
      value: boolean | FeatureConfig
    }) => {
      return userFeaturesApi.setFeature(userId, featureKey, { value })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-features', userId] })
      message.success('Feature updated successfully')
      setIsModalOpen(false)
      setEditingFeature(null)
      form.resetFields()
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to update feature'))
    },
  })

  const removeFeatureMutation = useMutation({
    mutationFn: async (featureKey: string) => {
      return userFeaturesApi.removeFeature(userId, featureKey)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-features', userId] })
      message.success('Feature removed successfully')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to remove feature'))
    },
  })

  const handleAdd = () => {
    setEditingFeature(null)
    form.resetFields()
    form.setFieldsValue({
      featureType: 'boolean',
      booleanValue: false,
      enabled: false,
    })
    setIsModalOpen(true)
  }

  const handleEdit = (featureKey: string, value: boolean | FeatureConfig) => {
    setEditingFeature(featureKey)

    if (typeof value === 'boolean') {
      form.setFieldsValue({
        featureKey,
        featureType: 'boolean',
        booleanValue: value,
      })
    } else {
      form.setFieldsValue({
        featureKey,
        featureType: 'complex',
        enabled: value.enabled,
        settings: value.settings ? JSON.stringify(value.settings, null, 2) : undefined,
        metadata: value.metadata ? JSON.stringify(value.metadata, null, 2) : undefined,
      })
    }

    setIsModalOpen(true)
  }

  const handleDelete = (featureKey: string) => {
    removeFeatureMutation.mutate(featureKey)
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()

      let featureValue: boolean | FeatureConfig

      if (values.featureType === 'boolean') {
        featureValue = values.booleanValue
      } else {
        featureValue = {
          enabled: values.enabled,
        }

        if (values.settings) {
          try {
            featureValue.settings = JSON.parse(values.settings)
          } catch (_e) {
            message.error('Invalid JSON in settings field')
            return
          }
        }

        if (values.metadata) {
          try {
            featureValue.metadata = JSON.parse(values.metadata)
          } catch (_e) {
            message.error('Invalid JSON in metadata field')
            return
          }
        }
      }

      setFeatureMutation.mutate({
        featureKey: values.featureKey,
        value: featureValue,
      })
    } catch (error) {
      console.error('Validation failed:', error)
    }
  }

  const featureType = Form.useWatch('featureType', form)

  interface FeatureRow {
    key: string
    featureKey: string
    value: boolean | FeatureConfig
    type: string
    enabled: boolean
  }

  const tableData: FeatureRow[] = data
    ? Object.entries(data).map(([key, value]) => ({
        key,
        featureKey: key,
        value,
        type: typeof value === 'boolean' ? 'boolean' : 'complex',
        enabled: typeof value === 'boolean' ? value : (value as FeatureConfig).enabled,
      }))
    : []

  const columns = [
    {
      title: 'Feature Key',
      dataIndex: 'featureKey',
      key: 'featureKey',
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: string) => (
        <Tag color={type === 'boolean' ? 'blue' : 'purple'}>
          {type === 'boolean' ? 'Boolean' : 'Complex'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 120,
      render: (enabled: boolean) => (
        <Tag
          color={enabled ? 'success' : 'default'}
          icon={enabled ? <CheckCircleOutlined /> : undefined}
        >
          {enabled ? 'Enabled' : 'Disabled'}
        </Tag>
      ),
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (value: boolean | FeatureConfig) => {
        if (typeof value === 'boolean') {
          return <Tag color={value ? 'success' : 'default'}>{value.toString()}</Tag>
        }

        const details = []
        if (value.settings) {
          details.push('settings')
        }
        if (value.metadata) {
          details.push('metadata')
        }

        return (
          <Space size={4}>
            <Tag color={value.enabled ? 'success' : 'default'}>
              enabled: {value.enabled.toString()}
            </Tag>
            {details.length > 0 && <Tag color="geekblue">+ {details.join(', ')}</Tag>}
          </Space>
        )
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: unknown, record: FeatureRow) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record.featureKey, record.value)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Remove Feature"
            description={`Are you sure you want to remove "${record.featureKey}"?`}
            onConfirm={() => handleDelete(record.featureKey)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <Card
      title="User Features"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Feature
        </Button>
      }
    >
      {tableData.length === 0 ? (
        <Empty description="No features configured for this user" />
      ) : (
        <Table
          dataSource={tableData}
          columns={columns}
          loading={isLoading}
          pagination={false}
          size="small"
        />
      )}

      <Modal
        title={editingFeature ? 'Edit Feature' : 'Add Feature'}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={() => {
          setIsModalOpen(false)
          setEditingFeature(null)
          form.resetFields()
        }}
        confirmLoading={setFeatureMutation.isPending}
        width={600}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
          <Form.Item
            name="featureKey"
            label="Feature Key"
            rules={[
              { required: true, message: 'Feature key is required' },
              {
                pattern: /^[a-zA-Z0-9_-]+$/,
                message: 'Only alphanumeric, dash, and underscore allowed',
              },
            ]}
          >
            <Input placeholder="e.g., aiXcel, transferV2" disabled={!!editingFeature} />
          </Form.Item>

          <Form.Item name="featureType" label="Feature Type">
            <Radio.Group disabled={!!editingFeature}>
              <Radio value="boolean">Boolean</Radio>
              <Radio value="complex">Complex Object</Radio>
            </Radio.Group>
          </Form.Item>

          {featureType === 'boolean' ? (
            <Form.Item name="booleanValue" label="Value" valuePropName="checked">
              <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
            </Form.Item>
          ) : (
            <>
              <Form.Item name="enabled" label="Enabled" valuePropName="checked">
                <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
              </Form.Item>

              <Form.Item name="settings" label="Settings (JSON)">
                <Input.TextArea
                  rows={4}
                  placeholder='{"key": "value"}'
                  style={{ fontFamily: 'monospace' }}
                />
              </Form.Item>

              <Form.Item name="metadata" label="Metadata (JSON)">
                <Input.TextArea
                  rows={4}
                  placeholder='{"key": "value"}'
                  style={{ fontFamily: 'monospace' }}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </Card>
  )
}
